/** Time/date helpers for history-style session lists.
 *
 *  Two flavors:
 *   - `formatHistoryTime` — single-string "Сегодня 12:45" / "Вчера 12:45"
 *     / "23.04.26", used in flat lists (hero "Недавние", spaces dashboard).
 *   - `groupByDate` + `formatRowTime` — bucketed groups with a sticky
 *     header carrying the date prefix; rows show only the redundant-free
 *     suffix (just "12:45" inside the today/yesterday groups).
 */

export type HistoryBucket =
  | "today"
  | "yesterday"
  | "week"
  | "month"
  | "older"
  | "unknown";

export interface HistoryGroup<T> {
  key: string;
  label: string;
  bucket: HistoryBucket;
  items: T[];
}

/** "Сегодня 12:45" / "Вчера 12:45" / "23.04.26" — for flat lists where
 *  no group header carries the date. */
export function formatHistoryTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";

  const now = new Date();
  const todayStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  const targetStart = new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
  ).getTime();
  const diffDays = Math.floor((todayStart - targetStart) / 86400000);

  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");

  if (diffDays === 0) return `Сегодня ${hh}:${mm}`;
  if (diffDays === 1) return `Вчера ${hh}:${mm}`;

  const dd = String(d.getDate()).padStart(2, "0");
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(2);
  return `${dd}.${mo}.${yy}`;
}

/** Inside a date-grouped list: "12:45" for today/yesterday (header carries
 *  the day), "DD.MM.YY" for older buckets. */
export function formatRowTime(iso: string, bucket: HistoryBucket): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  if (bucket === "today" || bucket === "yesterday") return `${hh}:${mm}`;
  const dd = String(d.getDate()).padStart(2, "0");
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(2);
  return `${dd}.${mo}.${yy}`;
}

/** Bucket items by their `getDate(item)` ISO string into Сегодня / Вчера
 *  / Предыдущие 7 дней / Предыдущие 30 дней / per-month groups. Preserves
 *  the input order within each bucket — assumes the input is already
 *  sorted newest-first.
 *
 *  Group order in the returned array follows insertion order, which
 *  matches the natural newest→oldest progression for newest-first input. */
export function groupByDate<T>(
  items: T[],
  getDate: (item: T) => string | null | undefined,
): HistoryGroup<T>[] {
  const now = new Date();
  const todayStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  const day = 86400000;
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  const map = new Map<string, HistoryGroup<T>>();
  const order: string[] = [];
  const pushTo = (
    key: string,
    label: string,
    bucket: HistoryBucket,
    item: T,
  ) => {
    let g = map.get(key);
    if (!g) {
      g = { key, label, bucket, items: [] };
      map.set(key, g);
      order.push(key);
    }
    g.items.push(item);
  };

  for (const it of items) {
    const iso = getDate(it);
    if (!iso) {
      pushTo("unknown", "Без даты", "unknown", it);
      continue;
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      pushTo("unknown", "Без даты", "unknown", it);
      continue;
    }
    const dStart = new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
    ).getTime();
    const diff = Math.floor((todayStart - dStart) / day);

    if (diff <= 0) pushTo("today", "Сегодня", "today", it);
    else if (diff === 1) pushTo("yesterday", "Вчера", "yesterday", it);
    else if (diff <= 7) pushTo("week", "Предыдущие 7 дней", "week", it);
    else if (diff <= 30) pushTo("month", "Предыдущие 30 дней", "month", it);
    else {
      const key = `m-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const monthName = cap(
        new Intl.DateTimeFormat("ru-RU", { month: "long" }).format(d),
      );
      const label =
        d.getFullYear() === now.getFullYear()
          ? monthName
          : `${monthName} ${d.getFullYear()}`;
      pushTo(key, label, "older", it);
    }
  }
  return order.map((k) => map.get(k)!);
}
